import { createORPCClient } from "@orpc/client";
import type { RouterContractClient } from "@orpc/contract";
import type { JsonifiedClient } from "@orpc/openapi";
import { OpenAPILink } from "@orpc/openapi/fetch";
import type {
  BasemapJob as BasemapJobSchema,
  BasemapResponse,
} from "@pax/api-contract/basemaps/v1alpha1";
import { contract } from "@pax/asset-editor-contract";
import {
  basemapPaths,
  HEARTBEAT,
  HEARTBEAT_INTERVAL_MS,
  messagesOf,
  PartJoiner,
  type Peer,
  type Presence,
  type RoomSnapshot,
  type ServerFrame,
  ServerFrameSchema,
  socketProtocol,
} from "@pax/asset-editor-contract/basemaps/v1alpha1";
import {
  BASEMAP_COARSEST_LEVEL,
  type BasemapDraftDocument,
  type BasemapFeature,
  type BasemapFeatureTile,
  BasemapManifest,
  blobToFeatures,
  childrenOf,
  decodeDetailMap,
  decodeFeatureTile,
  documentHash,
  downsample,
  emptyTile,
  featureSetTiles,
  featuresToBlob,
  type LeafTree,
  PmtilesReader,
  parentOf,
  placeKey,
  pmtilesAddress,
  type RasterType,
  type RoomExpectation,
  type RoomFeatureSetBase,
  type RoomOp,
  type RoomOverlay,
  type RoomRasterBase,
  RoomState,
  sha256,
  splitTile,
  type TilePixels,
  type TilePlace,
  tileKey,
  tileToBlob,
  tileTypeOf,
} from "@pax/basemap";
import { clientFetch } from "@pax/telemetry/client";
import { type TelemetryClientContext, traceProcedure } from "@pax/telemetry/orpc";
import type { z } from "zod";
import { api, reachable } from "@/lib/api";
import type { TileDecoder } from "./decoder";
import {
  readFeatureTileBytes,
  type StoredFeatureTile,
  storedFeatureTiles,
} from "./feature-drawing";
import type { Orientation } from "./rasters";
import { TiledRaster } from "./tiled";

/**
 * One open basemap draft, in its room in the asset editor alongside everyone else in it. The room
 * holds the draft: its base, its document, each raster's tree of leaves, the tiles changed in it,
 * each named by the hash of a blob stored with the API, and its overlays. This keeps a copy, loads tiles as they're
 * needed, and sends each action as a commit: its tiles' blobs uploaded first, then the commit
 * naming them, with the places above them made again from them. Actions show at once; the room's
 * order is what lasts. Undo is each person's own, and never takes back someone else's later change.
 */

type DraftRaster = BasemapDraftDocument["rasters"][string];
export type BasemapJob = z.infer<typeof BasemapJobSchema>;

export interface Published {
  manifest: BasemapManifest;
  /** Each file's tile URL template, by its hash, while it's public. */
  tiles: Record<string, string>;
  /**
   * Each file read by ranges through its signed URL, by its hash, for the owner of a basemap a
   * moderator took down, whose files have no public copies.
   */
  archives: Record<string, Promise<PmtilesReader>>;
}

/** A tile an action changed, with its pixels and blob before and after. */
export interface TileChange {
  raster: string;
  place: TilePlace;
  before: TilePixels | null;
  after: TilePixels | null;
}

/** One undoable action, and the ops that do and undo it. */
interface Step {
  summary: string;
  forward: RoomOp[];
  backward: RoomOp[];
  /** What undoing it expects to find, and what redoing it does. */
  afterward: RoomExpectation;
  beforehand: RoomExpectation;
  tiles: TileChange[];
  document?: { before: BasemapDraftDocument; after: BasemapDraftDocument };
  /** Whether it has been saved once, so redoing it expects what undoing it left. */
  done?: boolean;
}

/** What changed, for the editor to draw again. */
export type SessionEvent =
  | { kind: "tiles"; raster: string; places: TilePlace[] }
  /** A raster's leaves changed shape: places made finer, or one made coarser. */
  | { kind: "shape"; raster: string; merged: TilePlace[] }
  | { kind: "document" }
  | { kind: "peers"; peers: Peer[] }
  | { kind: "presence"; connection: string; state: Presence }
  | { kind: "rejected"; reason: string }
  | { kind: "overlays" }
  /** A raster's every tile may have changed, as when its base did. */
  | { kind: "raster"; raster: string }
  /** A feature set's features changed. */
  | { kind: "features"; featureSet: string }
  | { kind: "frozen" }
  | { kind: "thawed"; problem: string }
  /** The draft was published: it's that version now, and takes no more edits. */
  | { kind: "published"; versionId: string }
  | { kind: "connection"; connected: boolean };

/** The asset editor on this origin, which reads the sign-in cookie only to connect. */
const assetEditor = createORPCClient<
  JsonifiedClient<RouterContractClient<typeof contract, TelemetryClientContext>>
>(
  new OpenAPILink<TelemetryClientContext>(contract, {
    fetch: (request, init, options) =>
      (options.context.operation?.fetch ?? clientFetch)(request, {
        ...init,
        credentials: "same-origin",
      }),
  }),
  { interceptors: [traceProcedure] },
);

export class DraftSession {
  readonly assetId: string;
  readonly versionId: string;
  readonly state: RoomState;
  readonly rasters = new Map<string, TiledRaster>();
  readonly done: Step[] = [];
  readonly undone: Step[] = [];
  peers: Peer[] = [];
  you: string | null = null;
  frozen = false;
  /** The version the draft became, once it's published. */
  publishedAs: string | null = null;
  /** The tiles worker's URL template for each published file a base reads from, by its hash. */
  private readonly baseTiles: Record<string, string>;
  private baseTrees = new Map<string, LeafTree>();
  /** Each base file uploaded into the draft, read by ranges of its blob. */
  private readonly baseReaders = new Map<string, Promise<PmtilesReader>>();
  private readonly loading = new Map<string, Promise<TilePixels | null>>();
  private queue: Promise<unknown> = Promise.resolve();
  /** Commits sent and not yet back from the room, by id. */
  private readonly waiting = new Map<
    string,
    { resolve: () => void; reject: (error: Error) => void; step: Step; backward: boolean }
  >();
  private ws: WebSocket | null = null;
  private heartbeat: ReturnType<typeof setInterval> | undefined;
  private closed = false;
  private readonly joiner = new PartJoiner();

  private constructor(
    assetId: string,
    versionId: string,
    snapshot: RoomSnapshot,
    baseTiles: Record<string, string>,
    private readonly decoder: TileDecoder,
    private readonly listen: (event: SessionEvent) => void,
  ) {
    this.assetId = assetId;
    this.versionId = versionId;
    this.state = new RoomState(snapshot.document, snapshot);
    this.baseTiles = baseTiles;
    this.publishedAs = snapshot.published ? versionId : null;
    this.planRasters();
  }

  /** Opens a draft's room, resolving once it has said what the draft holds. */
  static async open(
    assetId: string,
    versionId: string,
    decoder: TileDecoder,
    listen: (event: SessionEvent) => void,
  ): Promise<DraftSession> {
    const draft = await reachable(api.basemaps.drafts.get({ params: { id: assetId, versionId } }));
    const welcome = await connect(versionId);
    const session = new DraftSession(
      assetId,
      versionId,
      welcome.room,
      draft.baseTiles,
      decoder,
      listen,
    );
    session.attach(welcome);
    return session;
  }

  /** The pictures the draft's authors trace, which are never published. */
  get overlays(): RoomOverlay[] {
    return this.state.overlays;
  }

  /** Whether a raster's tiles nobody changed are lossy, so repainting one encodes it again. */
  isLossy(raster: string): boolean {
    const rules = this.document.rasters[raster];
    return rules?.type === "color" && rules.encoding.codec === "avif";
  }

  get document(): BasemapDraftDocument {
    return this.state.document;
  }

  get revision(): number {
    return this.seq;
  }
  private seq = 0;

  get faces(): number {
    return this.document.geometry.kind === "sphere" ? 6 : 1;
  }

  get orientation(): Orientation {
    return this.document.geometry.kind === "sphere"
      ? (this.document.geometry.orientation as Orientation)
      : [
          [1, 0, 0],
          [0, 1, 0],
          [0, 0, 1],
        ];
  }

  /** A raster's tiles as the editor holds them, one per raster in the document. */
  private planRasters() {
    for (const name of [...this.rasters.keys()]) {
      if (!this.document.rasters[name]) this.rasters.delete(name);
    }
    for (const [name, rules] of Object.entries(this.document.rasters)) {
      const held = this.rasters.get(name);
      if (held && held.type === rules.type) continue;
      this.rasters.set(
        name,
        new TiledRaster(
          name,
          rules.type,
          () => this.state.tree(name) as LeafTree,
          this.orientation,
        ),
      );
    }
    this.baseTrees.clear();
    for (const [name, base] of this.state.bases) {
      this.baseTrees.set(name, decodeDetailMap(base.detailMap, base.finestLevel, this.faces));
    }
  }

  /* ── Tiles ───────────────────────────────────────────────────── */

  /**
   * A tile's pixels: its own blob if someone changed it, else its raster's base's there, enlarged
   * from the base's leaf where this draft went finer, else none. Loaded once, then held.
   */
  tile(raster: string, place: TilePlace): Promise<TilePixels | null> {
    const held = this.rasters.get(raster);
    const cached = held?.get(place);
    if (cached) return Promise.resolve(cached);
    const key = tileKey(raster, place);
    let pending = this.loading.get(key);
    if (!pending) {
      pending = this.readTile(raster, place).then(pixels => {
        this.loading.delete(key);
        if (pixels && held) held.set(place, pixels);
        return pixels;
      });
      this.loading.set(key, pending);
    }
    return pending;
  }

  private async readTile(raster: string, place: TilePlace): Promise<TilePixels | null> {
    const rules = this.document.rasters[raster];
    if (!rules) return null;
    // A place above edited leaves is made from them, as publishing does: the room keeps leaves.
    const held = this.rasters.get(raster);
    if (
      held &&
      !held.tree.isLeaf(place) &&
      this.state
        .changed()
        .some(tile => tile.raster === raster && isAtOrUnderPlace(tile.place, place))
    ) {
      return this.derive(raster, place);
    }
    const blob = this.state.blobOf(raster, place);
    if (blob) return this.decoder.decode(rules.type, await this.readBlob(blob), "blob");
    return this.baseTile(raster, rules.type, place);
  }

  /** The base's pixels at a place, enlarged from its leaf when the place is finer. */
  private async baseTile(
    raster: string,
    type: RasterType,
    place: TilePlace,
  ): Promise<TilePixels | null> {
    const base = this.state.bases.get(raster);
    const tree = this.baseTrees.get(raster);
    if (!base || !tree || base.rules.type !== type) return null;
    const across = 2 ** (place.level - 9);
    const leaf = tree.leafAt(place.face, (place.x + 0.5) / across, 1 - (place.y + 0.5) / across);
    if (leaf.level >= place.level) return this.readBaseTile(base, type, place);
    let pixels = await this.tile(raster, leaf);
    if (!pixels) return null;
    const rules = this.document.rasters[raster];
    const smooth =
      type === "color" ||
      (rules !== undefined && "enlarge" in rules && rules.enlarge === "bilinear");
    for (let level = leaf.level + 1; level <= place.level; level++) {
      const shift = place.level - level;
      const x = Math.floor(place.x / 2 ** shift);
      const y = Math.floor(place.y / 2 ** shift);
      pixels = splitTile(type, pixels, (y % 2) * 2 + (x % 2), smooth);
    }
    return pixels;
  }

  /** One stored tile of a base, decoded from its file's codec. */
  private async readBaseTile(
    base: RoomRasterBase,
    type: RasterType,
    place: TilePlace,
  ): Promise<TilePixels | null> {
    const tileType = tileTypeOf(base.rules);
    const { z, x, y } = pmtilesAddress(place, this.document.geometry.kind);
    if (base.storage === "published") {
      const template = this.baseTiles[base.tileFile];
      if (!template) return null;
      const response = await clientFetch(
        template.replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y)),
      );
      if (!response.ok) return null;
      return this.decoder.decode(type, await response.arrayBuffer(), tileType);
    }
    let reader = this.baseReaders.get(base.tileFile);
    if (!reader) {
      reader = PmtilesReader.open({
        read: async (offset, length) =>
          new Uint8Array(await this.readBlob(base.tileFile, { offset, length })),
      });
      this.baseReaders.set(base.tileFile, reader);
    }
    const bytes = await (await reader).getTile(z, x, y);
    return bytes ? this.decoder.decode(type, bytes.slice().buffer, tileType) : null;
  }

  /** A blob the draft holds, whole or one range of it. */
  async readBlob(sha: string, range?: { offset: number; length: number }): Promise<ArrayBuffer> {
    const response = await reachable(
      api.basemaps.drafts.blobs.get({
        params: { id: this.assetId, versionId: this.versionId, sha256: sha },
        headers: range ? { range: `bytes=${range.offset}-${range.offset + range.length - 1}` } : {},
      }),
    );
    return new Response(response.body as BodyInit).arrayBuffer();
  }

  /** Loads every tile in `places` that isn't held yet. */
  async need(raster: string, places: TilePlace[]): Promise<void> {
    await Promise.all(places.map(place => this.tile(raster, place)));
  }

  /* ── Actions ─────────────────────────────────────────────────── */

  /** Records a leaf's pixels before a stroke changes them, once a stroke. */
  snapshot(raster: TiledRaster, place: TilePlace, into: Map<string, TileChange>) {
    const key = tileKey(raster.name, place);
    if (into.has(key)) return;
    const pixels = raster.get(place);
    if (!pixels) return;
    into.set(key, { raster: raster.name, place, before: pixels.slice(), after: null });
  }

  /** Saves a stroke's leaves as they are now, and the places above them, as one commit. */
  commitStroke(summary: string, changes: Map<string, TileChange>) {
    const tiles = [...changes.values()].map(change => ({
      ...change,
      after: this.rasters.get(change.raster)?.get(change.place)?.slice() ?? null,
    }));
    return this.act(summary, tiles, [], []);
  }

  /** Changes the document: a layer added or deleted, a category named or colored. */
  changeDocument(summary: string, after: BasemapDraftDocument) {
    const before = this.document;
    return this.act(summary, [], [{ kind: "document", document: after }], [], {
      before,
      after,
    });
  }

  /** Fills every leaf of a raster with one value, as a new world's sea. */
  fill(summary: string, raster: string, value: number) {
    const held = this.rasters.get(raster);
    if (!held) return null;
    const tiles = held.tree.leaves().map(place => {
      const pixels = emptyTile(held.type).fill(value);
      return { raster, place, before: null, after: pixels };
    });
    return this.act(summary, tiles, [], []);
  }

  /* ── Files brought in ────────────────────────────────────────── */

  /** Stores a file the author brought in as a blob the draft holds, and says its hash. */
  async storeFile(file: Blob): Promise<string> {
    const sha = await sha256(new Uint8Array(await file.arrayBuffer()));
    await this.upload([{ sha, bytes: file }]);
    return sha;
  }

  /**
   * Makes a tile file, already stored, the base of raster `name`, which `after` adds or gives new
   * rules. Its tree becomes the file's detail and its changed tiles go back to the file's. Undo
   * puts the raster back as it was: its base, its finer places, and its changed tiles.
   */
  bringTileFile(
    summary: string,
    name: string,
    file: { sha256: string; finestLevel: number; detailMap: string },
    after: BasemapDraftDocument,
  ) {
    const before = this.document;
    const draftRules = after.rasters[name];
    if (!draftRules) return null;
    const { finestLevel: _, ...rules } = draftRules;
    const forward: RoomOp[] = [
      {
        kind: "base",
        raster: name,
        base: {
          tileFile: file.sha256,
          finestLevel: file.finestLevel,
          detailMap: file.detailMap,
          rules: rules as RoomRasterBase["rules"],
          storage: "draft",
        },
      },
    ];
    const backward: RoomOp[] = [];
    const previous = before.rasters[name];
    const tree = this.state.tree(name);
    if (previous && tree) {
      const base = this.state.bases.get(name) ?? null;
      backward.push({ kind: "base", raster: name, base });
      const baseSplits = new Set(
        (base ? decodeDetailMap(base.detailMap, base.finestLevel, this.faces).splits() : []).map(
          placeKey,
        ),
      );
      for (const split of tree.splits().sort((a, b) => a.level - b.level)) {
        if (!baseSplits.has(placeKey(split)))
          backward.push({ kind: "split", raster: name, place: split });
      }
      const changed = this.state.changed().filter(tile => tile.raster === name);
      for (let start = 0; start < changed.length; start += 4096) {
        backward.push({
          kind: "tiles",
          raster: name,
          writes: changed.slice(start, start + 4096).map(({ place, blob }) => ({ place, blob })),
        });
      }
    }
    return this.act(summary, [], forward, backward, { before, after });
  }

  /**
   * Makes `features` the whole of feature set `name`, which `after` adds or gives new rules, as one
   * blob. Undo puts back the features it had.
   */
  async bringFeatures(
    summary: string,
    name: string,
    features: BasemapFeature[],
    after: BasemapDraftDocument,
  ) {
    const before = this.document;
    const bytes = await featuresToBlob(features);
    const sha = await sha256(bytes);
    await this.upload([{ sha, bytes }]);
    this.featureCache.set(sha, Promise.resolve(features));
    const backward: RoomOp[] = before.featureSets[name]
      ? [{ kind: "features", featureSet: name, blob: this.state.features.get(name) ?? null }]
      : [];
    return this.act(summary, [], [{ kind: "features", featureSet: name, blob: sha }], backward, {
      before,
      after,
    });
  }

  /** A feature set's whole features, where the draft holds them, or null where they're its base's. */
  features(name: string): Promise<BasemapFeature[]> | null {
    const blob = this.state.features.get(name);
    if (!blob) return null;
    let features = this.featureCache.get(blob);
    if (!features) {
      features = this.readBlob(blob).then(bytes => blobToFeatures(new Uint8Array(bytes)));
      this.featureCache.set(blob, features);
    }
    return features;
  }
  private readonly featureCache = new Map<string, Promise<BasemapFeature[]>>();

  /**
   * A feature set's stored tiles, for drawing: clipped here from the draft's features, as
   * publishing clips them, or read from the base's tile file. Null for a set the draft doesn't have.
   */
  storedFeatures(name: string): ((place: TilePlace) => Promise<StoredFeatureTile | null>) | null {
    const rules = this.document.featureSets[name];
    const blob = this.state.features.get(name);
    const base = this.state.featureSetBases.get(name);
    const key = blob ?? base?.tileFile;
    if (!rules || !key) return null;
    const held = this.featureReaders.get(key);
    if (held) return held;
    let reader: (place: TilePlace) => Promise<StoredFeatureTile | null>;
    if (blob) {
      const geometry = this.document.geometry;
      const reading = (this.features(name) ?? Promise.resolve([])).then(async features => {
        const { tiles, tree } = await featureSetTiles(features, rules, geometry);
        const stored = new Map(tiles.map(tile => [`${tile.z}/${tile.x}/${tile.y}`, tile.bytes]));
        return storedFeatureTiles(tree, async place => {
          const { z, x, y } = pmtilesAddress(place, geometry.kind);
          const found = stored.get(`${z}/${x}/${y}`);
          return found ? decodeFeatureTile(found) : null;
        });
      });
      reader = async place => (await reading)(place);
    } else if (base) {
      const tree = decodeDetailMap(base.detailMap, BASEMAP_COARSEST_LEVEL, this.faces);
      reader = storedFeatureTiles(tree, place => this.readBaseFeatureTile(base, place));
    } else return null;
    this.featureReaders.set(key, reader);
    return reader;
  }
  private readonly featureReaders = new Map<
    string,
    (place: TilePlace) => Promise<StoredFeatureTile | null>
  >();

  /** One stored tile of a base's feature set. */
  private async readBaseFeatureTile(
    base: RoomFeatureSetBase,
    place: TilePlace,
  ): Promise<BasemapFeatureTile | null> {
    const { z, x, y } = pmtilesAddress(place, this.document.geometry.kind);
    if (base.storage === "published") {
      const template = this.baseTiles[base.tileFile];
      if (!template) return null;
      const response = await clientFetch(
        template.replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y)),
      );
      if (response.status !== 200) return null;
      return readFeatureTileBytes(new Uint8Array(await response.arrayBuffer()));
    }
    let reader = this.baseReaders.get(base.tileFile);
    if (!reader) {
      reader = PmtilesReader.open({
        read: async (offset, length) =>
          new Uint8Array(await this.readBlob(base.tileFile, { offset, length })),
      });
      this.baseReaders.set(base.tileFile, reader);
    }
    const bytes = await (await reader).getTile(z, x, y);
    return bytes ? readFeatureTileBytes(bytes) : null;
  }

  /** Removes a feature set, as `after` has it; undo puts back its features too. */
  removeFeatureSet(summary: string, name: string, after: BasemapDraftDocument) {
    const blob = this.state.features.get(name);
    return this.act(summary, [], [], blob ? [{ kind: "features", featureSet: name, blob }] : [], {
      before: this.document,
      after,
    });
  }

  /** Changes the overlays: one added, moved, faded, hidden, or removed. */
  changeOverlays(summary: string, overlays: RoomOverlay[]) {
    return this.act(
      summary,
      [],
      [{ kind: "overlays", overlays }],
      [{ kind: "overlays", overlays: this.state.overlays }],
    );
  }

  /** Makes leaves finer: each one four, a quarter of it at twice the detail. */
  async split(raster: string, places: TilePlace[]) {
    const held = this.rasters.get(raster);
    const rules = this.document.rasters[raster];
    if (!held || !rules) return null;
    const smooth = rules.type === "color" || ("enlarge" in rules && rules.enlarge === "bilinear");
    const tiles: TileChange[] = [];
    const structure: RoomOp[] = [];
    const undo: RoomOp[] = [];
    for (const place of places) {
      if (!held.tree.isLeaf(place)) continue;
      const pixels = await this.tile(raster, place);
      structure.push({ kind: "split", raster, place });
      undo.unshift({ kind: "merge", raster, place });
      childrenOf(place).forEach((child, quadrant) => {
        tiles.push({
          raster,
          place: child,
          before: null,
          after: pixels ? splitTile(rules.type, pixels, quadrant, smooth) : null,
        });
      });
    }
    if (structure.length === 0) return null;
    return this.act(
      places.length === 1 ? "Made a square finer" : `Made ${places.length} squares finer`,
      tiles,
      structure,
      undo,
    );
  }

  /** Makes a place one leaf again, from everything beneath it. */
  async merge(raster: string, place: TilePlace) {
    const held = this.rasters.get(raster);
    const rules = this.document.rasters[raster];
    if (!held || !rules || held.tree.isLeaf(place)) return null;
    const leaves = held.tree.leaves(place);
    await this.need(raster, leaves);
    const splits = held.tree.splits().filter(split => isAtOrUnderPlace(split, place));
    const pixels = await this.derive(raster, place);
    const tiles: TileChange[] = [
      { raster, place, before: null, after: pixels },
      ...leaves.map(leaf => ({
        raster,
        place: leaf,
        before: held.get(leaf)?.slice() ?? null,
        after: null,
      })),
    ];
    // Undoing it splits again from the top down, then puts each leaf back.
    const undo: RoomOp[] = [...splits]
      .sort((a, b) => a.level - b.level)
      .map(split => ({ kind: "split", raster, place: split }));
    return this.act("Made a square coarser", tiles, [{ kind: "merge", raster, place }], undo);
  }

  /**
   * The places above changed leaves made again from their children, finest first, and held. A view
   * zoomed out past the leaves draws these; the room keeps only leaves, and publishing makes its own.
   */
  async deriveAbove(
    raster: string,
    leaves: Iterable<TilePlace>,
  ): Promise<{ place: TilePlace; pixels: TilePixels }[]> {
    const held = this.rasters.get(raster);
    const rules = this.document.rasters[raster];
    if (!held || !rules) return [];
    const above = new Map<string, TilePlace>();
    for (const leaf of leaves) {
      for (let place = parentOf(leaf); place.level >= 9; place = parentOf(place)) {
        above.set(placeKey(place), place);
      }
    }
    const made: { place: TilePlace; pixels: TilePixels }[] = [];
    for (const place of [...above.values()].sort((a, b) => b.level - a.level)) {
      if (held.tree.isLeaf(place)) continue;
      const children = await Promise.all(childrenOf(place).map(child => this.tile(raster, child)));
      const pixels = downsample(rules.type, children, weightsOf(rules));
      held.set(place, pixels);
      made.push({ place, pixels });
    }
    return made;
  }

  /** A place's pixels made from its children, all the way down to its leaves. */
  private async derive(raster: string, place: TilePlace): Promise<TilePixels | null> {
    const held = this.rasters.get(raster);
    const rules = this.document.rasters[raster];
    if (!held || !rules) return null;
    if (held.tree.isLeaf(place)) return this.tile(raster, place);
    const children = await Promise.all(childrenOf(place).map(child => this.derive(raster, child)));
    return downsample(rules.type, children, weightsOf(rules));
  }

  /**
   * Does an action now, then saves it behind every action before it. `tiles` are the leaves it
   * changed; the places above them are made again from them and saved with them.
   */
  private act(
    summary: string,
    tiles: TileChange[],
    structure: RoomOp[],
    undoStructure: RoomOp[],
    document?: Step["document"],
  ) {
    const step: Step = {
      summary,
      forward: structure,
      backward: undoStructure,
      afterward: { tiles: [], leaves: [] },
      beforehand: { tiles: [], leaves: [] },
      tiles,
      ...(document ? { document } : {}),
    };
    this.done.push(step);
    this.undone.length = 0;
    this.show(step, false);
    return this.save(step, false);
  }

  undo() {
    const step = this.done.pop();
    if (!step) return null;
    this.undone.push(step);
    this.show(step, true);
    return this.save(step, true);
  }

  redo() {
    const step = this.undone.pop();
    if (!step) return null;
    this.done.push(step);
    this.show(step, false);
    return this.save(step, false);
  }

  /** Puts an action's pixels and shape in place here, before the room has it. */
  private show(step: Step, backward: boolean) {
    const touched = new Map<string, TilePlace[]>();
    const shaped = new Map<string, TilePlace[]>();
    const ops = backward ? step.backward : step.forward;
    if (ops.some(op => op.kind === "base")) {
      // A new base resets its raster's tree, so the ops go in the room's order: the document first.
      if (step.document) {
        const document = backward ? step.document.before : step.document.after;
        this.state.apply({ id: "", label: "", ops: [{ kind: "document", document }] });
      }
      this.state.apply({ id: "", label: "", ops });
      this.planRasters();
      this.listen({ kind: "document" });
      this.heardOps(ops);
      return;
    }
    for (const op of ops) {
      if (op.kind === "split" || op.kind === "merge") {
        const tree = this.state.tree(op.raster);
        try {
          if (op.kind === "split" && tree?.isLeaf(op.place)) tree.split(op.place);
          if (op.kind === "merge") tree?.merge(op.place);
        } catch {}
        const merged = shaped.get(op.raster) ?? [];
        if (op.kind === "merge") merged.push(op.place);
        shaped.set(op.raster, merged);
      }
    }
    for (const [raster, merged] of shaped) this.listen({ kind: "shape", raster, merged });
    for (const change of step.tiles) {
      const pixels = backward ? change.before : change.after;
      const held = this.rasters.get(change.raster);
      if (!held) continue;
      if (pixels) held.set(change.place, pixels);
      else held.drop(change.place);
      const list = touched.get(change.raster) ?? [];
      list.push(change.place);
      touched.set(change.raster, list);
    }
    if (step.document) {
      const document = backward ? step.document.before : step.document.after;
      this.state.apply({ id: "", label: "", ops: [{ kind: "document", document }] });
      this.planRasters();
      this.listen({ kind: "document" });
    }
    // A file brought in, or the overlays: applied as the room will, then drawn again.
    const brought = ops.filter(op => op.kind !== "split" && op.kind !== "merge");
    if (brought.length > 0) {
      this.state.apply({ id: "", label: "", ops: brought });
      this.heardOps(brought);
    }
    for (const [raster, places] of touched) this.listen({ kind: "tiles", raster, places });
  }

  /** Says what ops other than splits and merges changed, once the state has them. */
  private heardOps(ops: RoomOp[]) {
    for (const op of ops) {
      if (op.kind === "overlays") this.listen({ kind: "overlays" });
      if (op.kind === "features") this.listen({ kind: "features", featureSet: op.featureSet });
      if (op.kind === "base") {
        this.planRasters();
        this.rasters.get(op.raster)?.clear();
        this.listen({ kind: "document" });
        this.listen({ kind: "raster", raster: op.raster });
      }
      if (op.kind === "tiles") {
        const held = this.rasters.get(op.raster);
        for (const write of op.writes) held?.drop(write.place);
        this.listen({
          kind: "tiles",
          raster: op.raster,
          places: op.writes.map(write => write.place),
        });
      }
    }
  }

  /** Uploads an action's blobs and sends its commit, in turn, and waits for the room to take it. */
  private save(step: Step, backward: boolean): Promise<void> {
    const saved = this.queue.then(() => this.send(step, backward));
    this.queue = saved.catch(() => undefined);
    return saved;
  }

  private async send(step: Step, backward: boolean) {
    const writes = new Map<string, { raster: string; place: TilePlace; blob: string | null }>();
    const uploads: { sha: string; bytes: Uint8Array }[] = [];
    const store = async (raster: string, place: TilePlace, pixels: TilePixels | null) => {
      let blob: string | null = null;
      if (pixels) {
        const bytes = await tileToBlob(pixels);
        blob = await sha256(bytes);
        uploads.push({ sha: blob, bytes });
      }
      writes.set(tileKey(raster, place), { raster, place, blob });
    };
    // The trees as they are after this action, which `show` already made them.
    for (const change of step.tiles) {
      await store(change.raster, change.place, backward ? change.before : change.after);
    }
    await this.upload(uploads);

    const byRaster = new Map<string, { place: TilePlace; blob: string | null }[]>();
    for (const { raster, place, blob } of writes.values()) {
      const list = byRaster.get(raster) ?? [];
      list.push({ place, blob });
      byRaster.set(raster, list);
    }
    const tileOps: RoomOp[] = [...byRaster].map(([raster, list]) => ({
      kind: "tiles",
      raster,
      writes: list,
    }));
    const structure = backward ? step.backward : step.forward;
    const documentOp: RoomOp[] = step.document
      ? [{ kind: "document", document: backward ? step.document.before : step.document.after }]
      : [];
    // The document goes first, so a raster or feature set it adds exists for the ops after it.
    const ops = [...documentOp, ...structure.filter(op => op.kind !== "document"), ...tileOps];
    if (ops.length === 0) return;

    // Undo and redo expect to find what the step last left; the first time, nothing is expected.
    const expect = backward ? step.afterward : step.done ? step.beforehand : undefined;
    const leaves: RoomExpectation["leaves"] = [];
    const left: RoomExpectation = {
      tiles: [...writes.values()].map(({ raster, place, blob }) => ({ raster, place, blob })),
      leaves,
      ...(step.document
        ? { documentHash: documentHash(backward ? step.document.before : step.document.after) }
        : {}),
    };
    if (backward) step.beforehand = left;
    else step.afterward = left;
    step.done = true;

    const id = crypto.randomUUID();
    await new Promise<void>((resolve, reject) => {
      this.waiting.set(id, { resolve, reject, step, backward });
      this.sendFrame({
        type: "commit",
        commit: { id, label: step.summary, ops, ...(expect ? { expect } : {}) },
      });
    });
  }

  /** Stores blobs with the API, those the draft doesn't hold yet, and keeps them. */
  private async upload(blobs: { sha: string; bytes: Uint8Array | Blob }[]) {
    if (blobs.length === 0) return;
    const size = (bytes: Uint8Array | Blob) =>
      bytes instanceof Blob ? bytes.size : bytes.byteLength;
    for (let start = 0; start < blobs.length; start += 64) {
      const batch = blobs.slice(start, start + 64);
      const { uploads } = await reachable(
        api.basemaps.drafts.blobs.create({
          params: { id: this.assetId, versionId: this.versionId },
          body: {
            blobs: batch.map(blob => ({ sha256: blob.sha, bytes: size(blob.bytes) })),
          },
        }),
      );
      await Promise.all(
        uploads.map(async ({ sha256: sha, upload }) => {
          const blob = batch.find(entry => entry.sha === sha);
          if (!blob) return;
          const response = await clientFetch(upload.url, {
            method: upload.method,
            headers: upload.headers,
            body: blob.bytes as Uint8Array<ArrayBuffer> | Blob,
          });
          if (!response.ok) throw new Error(`A file didn't upload (${response.status}).`);
        }),
      );
      // Kept before the commit names them, so the room never names a blob the draft doesn't hold.
      await reachable(
        api.basemaps.drafts.blobs.keep({
          params: { id: this.assetId, versionId: this.versionId },
          body: { blobs: batch.map(blob => blob.sha) },
        }),
      );
    }
  }

  /** Waits for every action so far to be saved. */
  settled() {
    return this.queue;
  }

  /** Where you are and what you're doing, for everyone else in the draft. */
  present(state: Presence) {
    if (this.peers.length > 1) this.sendFrame({ type: "presence", state });
  }

  /** Publishes the draft as it is once every action is saved, with an optional label. */
  async publish(label?: string): Promise<BasemapJob> {
    await this.settled();
    return reachable(
      api.basemaps.drafts.publish({
        params: { id: this.assetId, versionId: this.versionId },
        body: { seq: this.seq, attestsCopyright: true, ...(label ? { label } : {}) },
      }),
    );
  }

  close() {
    this.closed = true;
    clearInterval(this.heartbeat);
    this.ws?.close();
  }

  /* ── The room ────────────────────────────────────────────────── */

  private attach(welcome: Welcome) {
    this.ws = welcome.ws;
    this.seq = welcome.room.seq;
    this.you = welcome.you.connection;
    this.peers = welcome.peers;
    this.frozen = welcome.frozen;
    this.ws.onmessage = event => this.receive(String(event.data));
    this.ws.onclose = () => this.dropped();
    clearInterval(this.heartbeat);
    this.heartbeat = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(HEARTBEAT.ping);
    }, HEARTBEAT_INTERVAL_MS);
    this.listen({ kind: "connection", connected: true });
    this.listen({ kind: "peers", peers: this.peers });
  }

  private dropped() {
    clearInterval(this.heartbeat);
    if (this.closed) return;
    this.listen({ kind: "connection", connected: false });
    const retry = (attempt: number) =>
      setTimeout(
        () => {
          if (this.closed) return;
          connect(this.versionId)
            .then(welcome => {
              this.rejoin(welcome.room);
              this.attach(welcome);
            })
            .catch(() => retry(attempt + 1));
        },
        Math.min(15_000, 500 * 2 ** attempt),
      );
    retry(0);
  }

  /** Takes the room as it is after a drop: the tiles that changed meanwhile are read again. */
  private rejoin(room: RoomSnapshot) {
    const before = new Map(
      this.state.changed().map(tile => [tileKey(tile.raster, tile.place), tile.blob]),
    );
    const next = new RoomState(room.document, room);
    this.state.document = next.document;
    this.state.overlays = next.overlays;
    this.state.trees.clear();
    for (const [name, tree] of next.trees) this.state.trees.set(name, tree);
    this.state.tiles.clear();
    for (const [key, blob] of next.tiles) this.state.tiles.set(key, blob);
    this.state.bases.clear();
    for (const [name, base] of next.bases) this.state.bases.set(name, base);
    this.state.featureSetBases.clear();
    for (const [name, base] of next.featureSetBases) this.state.featureSetBases.set(name, base);
    if (room.published) this.publishedAs = this.versionId;
    this.planRasters();
    const changed = new Map<string, TilePlace[]>();
    for (const key of new Set([...before.keys(), ...next.tiles.keys()])) {
      if (before.get(key) === next.tiles.get(key)) continue;
      const tile = room.tiles.find(entry => tileKey(entry.raster, entry.place) === key);
      if (tile) {
        this.rasters.get(tile.raster)?.drop(tile.place);
        changed.set(tile.raster, [...(changed.get(tile.raster) ?? []), tile.place]);
      }
    }
    this.listen({ kind: "document" });
    for (const [raster, places] of changed) this.listen({ kind: "tiles", raster, places });
    // A commit the room never answered is sent again; the room knows one it already took.
    for (const [id, waiting] of this.waiting) {
      this.waiting.delete(id);
      this.send(waiting.step, waiting.backward).then(waiting.resolve, waiting.reject);
    }
  }

  private receive(message: string) {
    if (message === HEARTBEAT.pong) return;
    let parsed = ServerFrameSchema.safeParse(safeJson(message));
    if (!parsed.success) return;
    if (parsed.data.type === "part") {
      const joined = this.joiner.take(parsed.data);
      if (!("json" in joined)) return;
      parsed = ServerFrameSchema.safeParse(safeJson(joined.json));
      if (!parsed.success) return;
    }
    this.handle(parsed.data);
  }

  private handle(frame: ServerFrame) {
    switch (frame.type) {
      case "commit": {
        const { commit } = frame;
        this.seq = commit.seq;
        const mine = this.waiting.get(commit.id);
        if (mine) {
          // Ours, as the room took it: the trees already show it; its blobs are now the draft's.
          // A new base resets its raster's tree, so a commit with one is taken again whole.
          this.waiting.delete(commit.id);
          const rebased = commit.ops.some(op => op.kind === "base");
          for (const op of commit.ops) {
            if (rebased || (op.kind !== "split" && op.kind !== "merge")) {
              this.state.apply({ ...commit, ops: [op] });
            }
          }
          mine.resolve();
          return;
        }
        this.state.apply({ id: commit.id, label: commit.label, ops: commit.ops });
        let documentChanged = false;
        for (const op of commit.ops) {
          if (op.kind === "document") documentChanged = true;
          if (op.kind === "overlays") this.listen({ kind: "overlays" });
          if (op.kind === "base") {
            this.rasters.get(op.raster)?.clear();
            documentChanged = true;
          }
          if (op.kind === "split" || op.kind === "merge") {
            this.listen({
              kind: "shape",
              raster: op.raster,
              merged: op.kind === "merge" ? [op.place] : [],
            });
          }
          if (op.kind === "tiles") {
            const held = this.rasters.get(op.raster);
            for (const write of op.writes) held?.drop(write.place);
            this.listen({
              kind: "tiles",
              raster: op.raster,
              places: op.writes.map(write => write.place),
            });
          }
        }
        if (documentChanged) {
          this.planRasters();
          this.listen({ kind: "document" });
        }
        return;
      }
      case "rejected": {
        const mine = this.waiting.get(frame.id);
        if (!mine) return;
        this.waiting.delete(frame.id);
        // Its pixels go back to what the room holds.
        const stack = mine.backward ? this.undone : this.done;
        const at = stack.lastIndexOf(mine.step);
        if (at >= 0) stack.splice(at, 1);
        for (const change of mine.step.tiles) this.rasters.get(change.raster)?.drop(change.place);
        for (const change of mine.step.tiles) {
          this.listen({ kind: "tiles", raster: change.raster, places: [change.place] });
        }
        this.listen({ kind: "rejected", reason: frame.reason });
        mine.reject(new Error(frame.reason));
        return;
      }
      case "peers":
        this.peers = frame.peers;
        this.listen({ kind: "peers", peers: frame.peers });
        return;
      case "presence":
        this.listen({ kind: "presence", connection: frame.connection, state: frame.state });
        return;
      case "frozen":
        this.frozen = true;
        this.listen({ kind: "frozen" });
        return;
      case "thawed":
        this.frozen = false;
        this.listen({ kind: "thawed", problem: frame.problem });
        return;
      case "published":
        this.publishedAs = frame.versionId;
        this.listen({ kind: "published", versionId: frame.versionId });
        return;
      default:
        return;
    }
  }

  private sendFrame(frame: object) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    for (const message of messagesOf(JSON.stringify(frame))) this.ws.send(message);
  }
}

interface Welcome {
  ws: WebSocket;
  room: RoomSnapshot;
  frozen: boolean;
  you: { connection: string; userId: string };
  peers: Peer[];
}

/** Asks for a token, opens the room's socket, and waits for it to say what the draft holds. */
async function connect(versionId: string): Promise<Welcome> {
  const { token } = await assetEditor.basemaps.connect({ params: { id: versionId } });
  const url = new URL(basemapPaths.socket(versionId), window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const ws = new WebSocket(url);
  const joiner = new PartJoiner();
  return new Promise((resolve, reject) => {
    ws.onopen = () =>
      ws.send(JSON.stringify({ type: "authenticate", token, protocol: socketProtocol }));
    ws.onerror = () => reject(new Error("The draft's room didn't answer."));
    ws.onclose = () => reject(new Error("The draft's room closed."));
    ws.onmessage = event => {
      let parsed = ServerFrameSchema.safeParse(safeJson(String(event.data)));
      if (parsed.success && parsed.data.type === "part") {
        const joined = joiner.take(parsed.data);
        if (!("json" in joined)) return;
        parsed = ServerFrameSchema.safeParse(safeJson(joined.json));
      }
      if (!parsed.success || parsed.data.type !== "welcome") return;
      const { room, frozen, you, peers } = parsed.data;
      ws.onopen = ws.onerror = ws.onclose = ws.onmessage = null;
      resolve({ ws, room, frozen, you, peers });
    };
  });
}

function weightsOf(rules: DraftRaster): Record<number, number> {
  return rules.type === "category"
    ? Object.fromEntries(
        Object.entries(rules.legend).map(([id, { weight }]) => [Number(id), weight ?? 1]),
      )
    : {};
}

function isAtOrUnderPlace(place: TilePlace, above: TilePlace): boolean {
  if (place.face !== above.face || place.level < above.level) return false;
  const shift = place.level - above.level;
  return (
    Math.floor(place.x / 2 ** shift) === above.x && Math.floor(place.y / 2 ** shift) === above.y
  );
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** One published tile, decoded, as readers see it. Null where the basemap has nothing. */
export async function readPublishedTile(
  published: Published,
  raster: string,
  type: RasterType,
  place: TilePlace,
  geometry: "sphere" | "plane",
  decoder: TileDecoder,
): Promise<TilePixels | null> {
  const bundleName = published.manifest.rasters[raster]?.bundle ?? raster;
  const file = published.manifest.bundles[bundleName]?.tileFile;
  const rules = published.manifest.rasters[raster];
  if (!file || !rules) return null;
  const { z, x, y } = pmtilesAddress(place, geometry);
  const template = published.tiles[file];
  const archive = published.archives[file];
  if (archive) {
    const bytes = await (await archive).getTile(z, x, y);
    return bytes ? decoder.decode(type, bytes.slice().buffer, tileTypeOf(rules)) : null;
  }
  if (!template) return null;
  const response = await clientFetch(
    template.replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y)),
  );
  if (!response.ok) return null;
  return decoder.decode(type, await response.arrayBuffer(), tileTypeOf(rules));
}

/** One stored tile of a published feature set, or null where the set has none. */
export async function readPublishedFeatureTile(
  published: Published,
  featureSet: string,
  place: TilePlace,
  geometry: "sphere" | "plane",
): Promise<BasemapFeatureTile | null> {
  const file = published.manifest.featureSets[featureSet]?.tileFile;
  if (!file) return null;
  const { z, x, y } = pmtilesAddress(place, geometry);
  const archive = published.archives[file];
  if (archive) {
    const bytes = await (await archive).getTile(z, x, y);
    return bytes ? readFeatureTileBytes(bytes) : null;
  }
  const template = published.tiles[file];
  if (!template) return null;
  const response = await clientFetch(
    template.replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y)),
  );
  if (response.status !== 200) return null;
  return readFeatureTileBytes(new Uint8Array(await response.arrayBuffer()));
}

/** A published basemap version, the one named or the asset's live version, and its tiles. */
export async function readPublished(assetId: string, versionId?: string): Promise<Published> {
  const basemap: z.infer<typeof BasemapResponse> = await reachable(
    api.basemaps.get({ params: { id: assetId }, query: versionId ? { version: versionId } : {} }),
  );
  const manifest = BasemapManifest.parse(await (await clientFetch(basemap.manifest.url)).json());
  return {
    manifest,
    tiles: Object.fromEntries(
      basemap.files.flatMap(file => (file.tiles ? [[file.sha256, file.tiles]] : [])),
    ),
    archives: Object.fromEntries(
      basemap.files.flatMap(file => {
        const { url } = file;
        if (!url) return [];
        const reader = PmtilesReader.open({
          read: async (offset, length) => {
            const response = await clientFetch(url, {
              headers: { range: `bytes=${offset}-${offset + length - 1}` },
            });
            return new Uint8Array(await response.arrayBuffer());
          },
        });
        return [[file.sha256, reader]];
      }),
    ),
  };
}

export function jobOf(assetId: string, jobId: string) {
  return reachable(api.basemaps.jobs.get({ params: { id: assetId, jobId } }));
}
